import React, { useEffect, useRef, useState } from 'react';
import { detectContinentFromBounds } from '../utils/continentMapping';

const InteractiveSVGMap = ({ onContinentClick }) => {
  const svgContainerRef = useRef(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoveredContinent, setHoveredContinent] = useState(null);
  const isLoadedRef = useRef(false); // Track if SVG is already loaded
  const [isReady, setIsReady] = useState(false); // Track if SVG is styled and ready

  useEffect(() => {
    // Load the SVG and make paths interactive
    const loadSVG = async () => {
      // Prevent reloading if already loaded
      if (isLoadedRef.current) return;
      
      try {
        const response = await fetch('/world-map-interactive.svg');
        const svgText = await response.text();
        
        if (svgContainerRef.current) {
          svgContainerRef.current.innerHTML = svgText;
          isLoadedRef.current = true; // Mark as loaded
          
          // Get the SVG element
          const svgElement = svgContainerRef.current.querySelector('svg');
          if (svgElement) {
            // Style the SVG for neon effect and prevent default fills
            svgElement.style.width = '120%';
            svgElement.style.height = '120%';
            svgElement.style.position = 'absolute';
            svgElement.style.top = '50%';
            svgElement.style.left = '50%';
            svgElement.style.transform = 'translate(-50%, -50%)';
            
            // Remove any default fills at SVG level
            svgElement.removeAttribute('fill');
            svgElement.style.fill = 'none';
            
            // Get all path elements (clean landmasses only)
            const paths = svgElement.querySelectorAll('path');
            
            paths.forEach((path, index) => {
              // Calculate bbox for continent detection
              const bbox = path.getBBox();
              
              // Apply BEATSEEK neon styling - invisible black fill for hover area, thinner strokes
              // Set both style and attribute to ensure it persists
              path.style.fill = 'rgba(0, 0, 0, 0.01)'; // Nearly invisible black - makes landmass hoverable
              path.setAttribute('fill', 'rgba(0, 0, 0, 0.01)');
              path.setAttribute('stroke', '#00FFFF');
              path.setAttribute('stroke-width', '0.5'); // Thinner
              path.style.cursor = 'pointer';
              path.style.transition = 'all 0.3s ease';
              path.style.pointerEvents = 'auto';
              path.style.filter = 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.6))';
              
              // Aggressively remove any default fills/styles that might reappear
              path.removeAttribute('class');
              path.removeAttribute('style'); // Remove inline styles first
              
              // Then reapply our custom styles (must come after removeAttribute)
              path.style.fill = 'rgba(0, 0, 0, 0.01)';
              path.style.cursor = 'pointer';
              path.style.transition = 'all 0.3s ease';
              path.style.pointerEvents = 'auto';
              path.style.filter = 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.6))';
              
              // Hover effect
              path.addEventListener('mouseenter', (e) => {
                const continent = detectContinentFromBounds(bbox);
                
                setHoveredCountry(index);
                setHoveredContinent(continent);
                
                // Magenta fill (forced) with cyan stroke and even tighter glow
                path.style.fill = 'rgba(255, 0, 255, 0.7)'; // Use style instead of setAttribute
                path.setAttribute('stroke', '#00FFFF');
                path.setAttribute('stroke-width', '0.75');
                path.style.filter = 'drop-shadow(0 0 6px rgba(255, 0, 255, 0.9)) drop-shadow(0 0 10px rgba(0, 255, 255, 0.5))';
              });
              
              path.addEventListener('mouseleave', (e) => {
                setHoveredCountry(null);
                setHoveredContinent(null);
                
                // Reset to base cyan neon style with invisible fill
                path.style.fill = 'rgba(0, 0, 0, 0.01)'; // Invisible but hoverable
                path.setAttribute('stroke', '#00FFFF');
                path.setAttribute('stroke-width', '0.5');
                path.style.filter = 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.6))';
              });
              
              // Click handler
              path.addEventListener('click', (e) => {
                e.stopPropagation();
                const bbox = path.getBBox();
                const continent = detectContinentFromBounds(bbox);
                
                if (continent.id !== 'unknown') {
                  onContinentClick(continent.id, continent.name);
                }
              });
            });
          }
          
          // Mark as ready after all styling is applied
          setIsReady(true);
        }
      } catch (error) {
        console.error('Error loading SVG:', error);
      }
    };
    
    loadSVG();
  }, [onContinentClick]);

  return (
    <>
      <div 
        ref={svgContainerRef}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          pointerEvents: 'auto',
          background: 'radial-gradient(ellipse at center, #0a1929 0%, #020912 100%)',
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
      />
      
      {/* Hover label */}
      {hoveredContinent && hoveredContinent.id !== 'unknown' && (
        <div style={{
          position: 'absolute',
          top: '15px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#00FFFF',
          fontSize: '16px',
          fontWeight: '600',
          textShadow: '0 0 15px rgba(0, 255, 255, 0.9)',
          pointerEvents: 'none',
          zIndex: 1000,
          textAlign: 'center',
          letterSpacing: '1px'
        }}>
          {hoveredContinent.name}
        </div>
      )}
    </>
  );
};

export default InteractiveSVGMap;
